import { useState, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { getConfig } from '../config/constants'

interface Admin {
  id: string
  name: string
  email: string
  role: 'admin' | 'super_admin'
  avatar?: string
}

interface AuthState {
  isAuthenticated: boolean
  admin: Admin | null
  loading: boolean
}

// Configuração da API
const config = getConfig()
const API_BASE_URL = config.API_URL

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    admin: null,
    loading: true
  })

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('admin_token')
      
      if (!token) {
        setAuthState({
          isAuthenticated: false,
          admin: null,
          loading: false
        })
        return
      }

      console.log('🔍 Verificando auth com URL:', `${API_BASE_URL}/admin/me`)
      const response = await fetch(`${API_BASE_URL}/admin/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Token inválido')
      }

      const admin = await response.json()
      
      setAuthState({
        isAuthenticated: true,
        admin,
        loading: false
      })
    } catch (error) {
      console.error('Erro na verificação de autenticação:', error)
      localStorage.removeItem('admin_token')
      setAuthState({
        isAuthenticated: false,
        admin: null,
        loading: false
      })
    }
  }

  const login = async (email: string, password: string) => {
    try {
      console.log('🔍 Tentando login com URL:', `${API_BASE_URL}/admin/login`)
      const response = await fetch(`${API_BASE_URL}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Erro ao fazer login')
      }

      const data = await response.json()
      
      localStorage.setItem('admin_token', data.token)
      
      setAuthState({
        isAuthenticated: true,
        admin: data.admin,
        loading: false
      })

      toast.success('Login realizado com sucesso!')
      return true
    } catch (error) {
      console.error('Erro no login:', error)
      toast.error(error instanceof Error ? error.message : 'Erro ao fazer login')
      return false
    }
  }

  const loginWithGoogle = async (credential: string) => {
    try {
      console.log('🔍 Login Google com URL:', `${API_BASE_URL}/auth/google`)
      const response = await fetch(`${API_BASE_URL}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          token: credential,
          type: 'admin'
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Erro na autenticação Google')
      }

      const data = await response.json()
      
      // Verificar se o usuário tem permissão de admin
      if (data.user.role !== 'admin') {
        throw new Error('Você não tem permissão para acessar o painel administrativo')
      }
      
      localStorage.setItem('admin_token', data.token)
      
      setAuthState({
        isAuthenticated: true,
        admin: {
          id: data.user.id,
          name: data.user.name,
          email: data.user.email,
          role: data.user.role,
          avatar: data.user.avatar
        },
        loading: false
      })

      toast.success('Login com Google realizado com sucesso!')
      return true
    } catch (error) {
      console.error('Erro no login com Google:', error)
      toast.error(error instanceof Error ? error.message : 'Erro na autenticação Google')
      return false
    }
  }

  const logout = () => {
    localStorage.removeItem('admin_token')
    setAuthState({
      isAuthenticated: false,
      admin: null,
      loading: false
    })
    toast.success('Logout realizado com sucesso!')
  }

  const updateProfile = async (data: Partial<Admin>) => {
    try {
      const token = localStorage.getItem('admin_token')
      
      console.log('🔍 Atualizando perfil com URL:', `${API_BASE_URL}/admin/profile`)
      const response = await fetch(`${API_BASE_URL}/admin/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        throw new Error('Erro ao atualizar perfil')
      }

      const updatedAdmin = await response.json()
      
      setAuthState(prev => ({
        ...prev,
        admin: updatedAdmin
      }))

      toast.success('Perfil atualizado com sucesso!')
      return true
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error)
      toast.error('Erro ao atualizar perfil')
      return false
    }
  }

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      const token = localStorage.getItem('admin_token')
      
      console.log('🔍 Alterando senha com URL:', `${API_BASE_URL}/admin/change-password`)
      const response = await fetch(`${API_BASE_URL}/admin/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ currentPassword, newPassword })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Erro ao alterar senha')
      }

      toast.success('Senha alterada com sucesso!')
      return true
    } catch (error) {
      console.error('Erro ao alterar senha:', error)
      toast.error(error instanceof Error ? error.message : 'Erro ao alterar senha')
      return false
    }
  }

  return {
    ...authState,
    login,
    loginWithGoogle,
    logout,
    updateProfile,
    changePassword,
    refreshAuth: checkAuth
  }
} 